/**
 *
 * There are various permutations for how we can initiate a new conversation (see: ConversationType & ConversationRouterState)
 * There are various surfaces that can trigger a conversation; some initiate a conversation with the same parameteres (see CtaSource)
 *
 * To create a new entry point:
 * 1. Add a new enum CtaSource that represents the new triggering surface
 * 2. See if there is a ConversationRouterState that matches data you'd need to seed
 * 3. Update the switch/case statement & overload args in buildConversationRouterState
 */
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { AgentId, WorkflowAgentRouteState } from "../types";

/** CtaSource represent the various surfaces that will call into the assistant page
 */
export enum CtaSource {
  OneClickAskPrompt = "one_click_ask_prompt",
  // This will come as M1
  // OneClickWritePrompt = 'one_click_write_prompt',
  OmniboxAskQuery = "omnibox_ask_query",
  // ask suggestions with sources that are used for generating suggestions
  OmniboxAskFromSuggestion = "omnibox_ask_from_suggestion",
  OmniboxDraftWithTemplate = "omnibox_draft_with_template",
  OmniboxDraftWithTone = "omnibox_draft_with_tone",
  OmniboxDraftWithDocument = "omnibox_draft_with_document",
  OmniboxDraftWithMessage = "omnibox_draft_with_message",
  // SERP
  SerpSummarize = "serp_summarize",
  // Stack
  StackChat = "stack_chat",
  StackSuggestedPrompt = "stack_suggested_prompt",
  StackSummarize = "stack_summarize",
  StackItemSummarize = "stack_item_summarize",
  // Start page
  FeedItemSummarize = "feed_item_summarize",
  RecentSummarize = "recent_summarize",
  StartPageStackChat = "start_page_stack_chat",
  // Legacy
  LegacySession = "legacy_session",
}

// ====================== ConversationTypes and isXFn() utilities ======================
// ConversationType are all the permutations that we'd seed a conversation when we navigate to the assistant
// these are utility types to ensure we have type safety when reading data from router state
export enum ConversationType {
  Ask = "Ask",
  AskFromSuggestion = "AskFromSuggestion",
  WriteFromTemplate = "WriteFromTemplate",
  WriteFromTone = "WriteFromTone",
  WriteWithDocument = "WriteWithDocument",
  WriteWithMessage = "WriteWithMessage",
  AskWithFollowup = "AskWithFollowup",
  AskWithMultiDocs = "AskWithMultiDocs",
  AskWithPromptMultiDocs = "AskWithPromptMultiDocs",
  SummarizeItem = "SummarizeItem",
  LegacySession = "LegacySession",
}

export type ConversationRouterState =
  | AskConversationRouterState
  | AskFromSuggestionConversationRouterState
  | WriteFromTemplateRouterState
  | WriteFromToneRouterState
  | WriteWithDocumentRouterState
  | WriteWithMessageRouterState
  | AskWithFollowupRouterState
  | SummarizeItemRouterState
  | AskWithMultiDocsRouterState
  | AskWithPromptMultiDocsRouterState
  | LegacySessionRouterState;

// Set of dynamic params we can specify for creating a new conversation router state
// Intentionally not including skipSearch as this is better set by builder function
// in ensure a single source of truth
type DynamicParams = {
  // don't include skipSearch
  message: string;
  templateId: string;
  voiceId: string;
  attachments: string[];
  agentIds: AgentId[];
  chatSources: ChatSource[];
  conversationId: string; // passing this up front means we already have a converation; one was created
  scenarioMeta: ScenarioMeta;
  serverConversationId: string;
  messageHistory?: MessageHistory[]; // Added for legacy sessions
  markdownContent?: string; // Added for legacy sessions markdown draft
};
export type ScenarioMeta =
  | {
      type: "stack";
      sharingId: string;
    }
  | {
      type: "stack_item_summarize";
      stackSharingId: string;
    }
  | {
      type: "search";
      query?: string;
      searchSessionId: string | undefined;
      searchQueryUuid: string | undefined;
    }
  | { type: "feed" }
  | {
      type: "omnibox_summarize_shortcut";
      query?: string;
      searchSessionId: string | undefined;
      // NOTE: we don't pass in searchQueryUuid here because searchQueryUuid
      // is more of a SERP concept
    }
  | { type: "other" };

interface BaseRouterState {
  ctaSource: CtaSource;
  conversationType: ConversationType;
  scenarioMeta?: ScenarioMeta;
}

// ========== Conversation Type Specific Router States ========
/** AskConversationRouterState
 *  used in various surfaces such as the one-click ask prompt, omnibox ask query, etc.
 *
 */
interface AskConversationRouterState extends BaseRouterState {
  conversationType: ConversationType.Ask;
  message: string;
}

export type AskParams = Pick<DynamicParams, "message">;

export function isAskConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.Ask,
  );
}

interface AskFromSuggestionConversationRouterState extends BaseRouterState {
  conversationType: ConversationType.AskFromSuggestion;
  message: string;
  attachments: string[]; // NOTE: these are the sources used for generating suggestions
  agentIds: AgentId[];
}

export type AskFromSuggestionParams = Pick<
  DynamicParams,
  "message" | "attachments" | "agentIds"
>;

export function isAskFromSuggestionConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskFromSuggestionConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskFromSuggestion,
  );
}

/** AskWithMultiDocsRouterState
 *  used in the stack chat surface, supports future areas where we might want to pass in multiple uuids
 *
 */
interface AskWithMultiDocsRouterState extends BaseRouterState {
  chatSources: ChatSource[];
  conversationType: ConversationType.AskWithMultiDocs;
  scenarioMeta: ScenarioMeta;
}

// we need to export the type to ensure we satisify args when building router state
export type AskWithMultiDocsParams = Pick<
  DynamicParams,
  "chatSources" | "scenarioMeta"
>;

export function isAskWithMultiDocsConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskWithMultiDocsRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskWithMultiDocs,
  );
}

/** AskWithPromptMultiDocsRouterState
 *  Used when coming from the Stack surface via a suggested query; Supports passing a message alongside a collection of uuid sources.
 *
 */
interface AskWithPromptMultiDocsRouterState extends BaseRouterState {
  chatSources: ChatSource[];
  conversationType: ConversationType.AskWithPromptMultiDocs;
  message: string;
  scenarioMeta: ScenarioMeta;
}

export type AskWithPromptMultiDocsParams = Pick<
  DynamicParams,
  "chatSources" | "message" | "scenarioMeta"
>;

export function isAskWithPromptMultiDocsConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskWithPromptMultiDocsRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskWithPromptMultiDocs,
  );
}

/** WriteWithMessageRouterState
 *  used in the omnibox draft with message surface
 *
 */
interface WriteWithMessageRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteWithMessage;
  message: string;
  agentIds: AgentId[];
}

export type WriteWithMessageParams = Pick<DynamicParams, "message">;

export function isWriteWithMessageConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteWithMessageRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteWithMessage,
  );
}

/** WriteFromTemplateRouterState
 *  used in the omnibox draft with template surface
 *
 */
interface WriteFromTemplateRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteFromTemplate;
  templateId: string;
  agentIds: AgentId[];
}

export type WriteFromTemplateParams = Pick<DynamicParams, "templateId">;

export function isWriteFromTemplateConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteFromTemplateRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteFromTemplate,
  );
}

/**
 * WriteFromToneRouterState
 *  used in the omnibox draft with tone surface
 *
 */
interface WriteFromToneRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteFromTone;
  voiceId: string;
  agentIds: AgentId[];
}

export type WriteFromToneParams = Pick<DynamicParams, "voiceId">;

export function isWriteFromToneConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteFromToneRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteFromTone,
  );
}

/** WriteWithDocumentRouterState
 *  used in the omnibox draft with document surface, but could be used on other
 *  surfaces like feed etc.
 *
 */
interface WriteWithDocumentRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteWithDocument;
  chatSources: ChatSource[];
  agentIds: AgentId[];
}

export type WriteWithDocumentParams = Pick<DynamicParams, "chatSources">;

export function isWriteWithDocumentConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteWithDocumentRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteWithDocument,
  );
}

/** SummarizeItemRouterState
 *  used in the serp summarize surface, but also for stack_item summarization and feed items
 *
 */
interface SummarizeItemRouterState extends BaseRouterState {
  conversationType: ConversationType.SummarizeItem;
  chatSources: ChatSource[];
  scenarioMeta: ScenarioMeta;
}

export type SummarizeItemParams = Pick<
  DynamicParams,
  "chatSources" | "scenarioMeta"
>;

export function isSummarizeItemConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is SummarizeItemRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.SummarizeItem,
  );
}

/** AskWithFollowupRouterState
 *  used in the serp answer follow up surface (currently not integrated with follow-up)
 *
 */
interface AskWithFollowupRouterState extends BaseRouterState {
  conversationType: ConversationType.AskWithFollowup;
  // NOTE: Until persistence comes in, we may need to send cached message history
  // history: messages,
  conversationId: string;
  message: string;
  scenarioMeta: ScenarioMeta;
}

export type AskWithFollowupParams = Pick<
  DynamicParams,
  "conversationId" | "message" | "scenarioMeta"
>;

export function isAskWithFollowupConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskWithFollowupRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskWithFollowup,
  );
}

// small util function to ensure we have the necessary properties in the params
export const requiredPropertiesExist = <K extends keyof DynamicParams>(
  params: Partial<DynamicParams>,
  properties: Array<K>,
): params is Pick<DynamicParams, K> => {
  for (const property of properties) {
    if (params[property] === undefined) {
      return false;
    }
  }
  return true;
};

/**
 * Builds a conversation router state based on the source and params
 */
// === Ask Params ===
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxAskQuery,
  params: AskParams,
): AskConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OneClickAskPrompt,
  params: AskFromSuggestionParams,
): AskFromSuggestionConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OneClickAskPrompt,
  params: AskFromSuggestionParams,
): AskFromSuggestionConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxAskFromSuggestion,
  params: AskFromSuggestionParams,
): AskFromSuggestionConversationRouterState;
// === Ask with multiple docs ===
export function buildConversationRouterState(
  ctaSource: CtaSource.StackChat | CtaSource.StartPageStackChat,
  params: AskWithMultiDocsParams,
): AskWithMultiDocsRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.StackSuggestedPrompt,
  params: AskWithPromptMultiDocsParams,
): AskWithPromptMultiDocsRouterState;
// === Write ===
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithTemplate,
  params: WriteFromTemplateParams,
): WriteFromTemplateRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithTone,
  params: WriteFromToneParams,
): WriteFromToneRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithDocument,
  params: WriteWithDocumentParams,
): WriteWithDocumentRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithMessage,
  params: WriteWithMessageParams,
): WriteWithMessageRouterState;
// === Summarize ===
export function buildConversationRouterState(
  ctaSource:
    | CtaSource.SerpSummarize
    | CtaSource.StackItemSummarize
    | CtaSource.StackSummarize
    | CtaSource.FeedItemSummarize
    | CtaSource.RecentSummarize,
  params: SummarizeItemParams,
): SummarizeItemRouterState;
// === Legacy Session ===
export function buildConversationRouterState(
  ctaSource: CtaSource.LegacySession,
  params: LegacySessionParams,
): LegacySessionRouterState;
// === default constructor we overrride ===
export function buildConversationRouterState(
  ctaSource: CtaSource,
  params: Partial<DynamicParams>,
): ConversationRouterState {
  switch (ctaSource) {
    /**
     *  ========== Open ended agendId cases ==========
     */
    case CtaSource.OmniboxAskQuery:
      if (!requiredPropertiesExist(params, ["message"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.Ask,
        message: params.message,
      } as const satisfies AskConversationRouterState;

    case CtaSource.OmniboxAskFromSuggestion:
    case CtaSource.OneClickAskPrompt: {
      if (
        !requiredPropertiesExist(params, ["message", "attachments", "agentIds"])
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.AskFromSuggestion,
        message: params.message,
        attachments: params.attachments,
        agentIds: params.agentIds,
      } as const satisfies AskFromSuggestionConversationRouterState;
    }

    case CtaSource.StackChat:
    case CtaSource.StartPageStackChat: {
      if (!requiredPropertiesExist(params, ["chatSources", "scenarioMeta"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        chatSources: params.chatSources,
        conversationType: ConversationType.AskWithMultiDocs,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies AskWithMultiDocsRouterState;
    }

    case CtaSource.StackSuggestedPrompt: {
      if (
        !requiredPropertiesExist(params, [
          "chatSources",
          "message",
          "scenarioMeta",
        ])
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        chatSources: params.chatSources,
        conversationType: ConversationType.AskWithPromptMultiDocs,
        message: params.message,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies AskWithPromptMultiDocsRouterState;
    }

    /**
     *  ========== Write agendId cases ==========
     */
    case CtaSource.OmniboxDraftWithMessage: {
      if (!requiredPropertiesExist(params, ["message"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteWithMessage,
        message: params.message,
        agentIds: ["write"],
      } as const satisfies WriteWithMessageRouterState;
    }

    case CtaSource.OmniboxDraftWithTemplate: {
      if (!requiredPropertiesExist(params, ["templateId"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteFromTemplate,
        templateId: params.templateId,
        agentIds: ["write"],
      } as const satisfies WriteFromTemplateRouterState;
    }

    case CtaSource.OmniboxDraftWithTone: {
      if (!requiredPropertiesExist(params, ["voiceId"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteFromTone,
        voiceId: params.voiceId,
        agentIds: ["write"],
      } as const satisfies WriteFromToneRouterState;
    }

    case CtaSource.OmniboxDraftWithDocument: {
      if (!requiredPropertiesExist(params, ["chatSources"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteWithDocument,
        chatSources: params.chatSources,
        agentIds: ["write"],
      } as const satisfies WriteWithDocumentRouterState;
    }

    /**
     *  ========== Summarize cases ==========
     */
    case CtaSource.SerpSummarize:
    case CtaSource.StackItemSummarize:
    case CtaSource.StackSummarize:
    case CtaSource.FeedItemSummarize:
    case CtaSource.RecentSummarize: {
      if (!requiredPropertiesExist(params, ["chatSources", "scenarioMeta"])) {
        throw new Error("Missing required property");
      }

      return {
        ctaSource,
        conversationType: ConversationType.SummarizeItem,
        chatSources: params.chatSources,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies SummarizeItemRouterState;
    }

    /**
     *  ========== Legacy Session cases ==========
     */
    case CtaSource.LegacySession: {
      if (!requiredPropertiesExist(params, ["messageHistory"])) {
        throw new Error("Missing required property");
      }
      // Ensure messageHistory is not undefined
      const { messageHistory, markdownContent } = params;
      if (!messageHistory) {
        throw new Error("Message history is required for legacy sessions");
      }
      return {
        ctaSource,
        conversationType: ConversationType.LegacySession,
        messageHistory,
        markdownContent,
      } as const satisfies LegacySessionRouterState;
    }

    default:
      throw new Error(`Unhandled source: ${ctaSource}`);
  }
}

// Define a message history interface for legacy sessions
export interface MessageHistory {
  role: "user" | "assistant";
  content: string;
  attachments?: ChatSource[]; // Using ChatSource instead of any
}

/** LegacySessionRouterState
 *  Used when importing a conversation from a legacy session
 */
interface LegacySessionRouterState extends BaseRouterState {
  conversationType: ConversationType.LegacySession;
  messageHistory: MessageHistory[];
  markdownContent?: string; // Optional markdown content from legacy session
}

export type LegacySessionParams = Pick<
  DynamicParams,
  "messageHistory" | "markdownContent"
>;

export function isLegacySessionConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is LegacySessionRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.LegacySession,
  );
}
